import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Rating from "@mui/material/Rating";
import Stack from "@mui/material/Stack";
import {
  buttonStyle,
  secondaryButtonStyle,
  title as titleStyle,
  contentStyle,
} from "../ModuleFeedback";
import { submitPathFeedback } from "../../services/AssignmentService";

/**
 * Shown once, right after a learner downloads the certificate for a
 * completed learning path — captures how the path felt while it's fresh.
 * Skippable; never reappears once submitted (caller checks that via
 * hasSubmittedPathFeedback before rendering this at all).
 */
export default function LearningPathFeedbackDialog({
  open,
  onClose,
  learningPathId,
  pathTitle,
}) {
  const [feedback, setFeedback] = React.useState("");
  const [rating, setRating] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSkip = () => {
    setFeedback("");
    setRating(0);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    await submitPathFeedback(learningPathId, rating || 5, feedback);
    setSubmitting(false);
    setFeedback("");
    setRating(0);
    onClose();
  };

  return (
    <Dialog
      sx={{ borderRadius: 5 }}
      open={open}
      onClose={handleSkip}
      aria-labelledby="lp-feedback-title"
      aria-describedby="lp-feedback-description"
    >
      <DialogTitle sx={titleStyle} id="lp-feedback-title">
        How was “{pathTitle}”?
      </DialogTitle>
      <DialogContent sx={{ width: "300px" }}>
        <DialogContentText sx={contentStyle} id="lp-feedback-description">
          You just earned your certificate — a quick rating helps us improve this path.
        </DialogContentText>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ marginTop: 2 }}>
          <Rating
            name="lp-feedback-rating"
            sx={{ fontSize: "30px" }}
            value={rating}
            onChange={(event, newValue) => setRating(newValue)}
          />
        </Stack>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Anything you'd add? (optional)"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          sx={{ marginTop: 2, borderRadius: "20px" }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.5,
          py: 2,
        }}
      >
        <Button onClick={handleSkip} sx={secondaryButtonStyle} disabled={submitting}>
          Skip
        </Button>
        <Button onClick={handleSubmit} sx={buttonStyle} disabled={submitting} autoFocus>
          {submitting ? "Submitting…" : "Submit feedback"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
